import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse } from '../api/helpers';
import { inputStyle, labelStyle, modalWrap, primaryBtn, cancelBtn, successAlert, errorAlert, pillBtn, infoBox } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';
const ROLES   = ['reception', 'doctor', 'nurse', 'hospital-admin'];

const ROLE_COLORS = {
  'super-admin':    { bg: 'rgba(245,158,11,0.15)',  text: '#fbbf24' },
  'hospital-admin': { bg: 'rgba(14,165,233,0.15)',  text: '#38bdf8' },
  'doctor':         { bg: 'rgba(34,197,94,0.15)',   text: '#4ade80' },
  'reception':      { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8' },
  'nurse':          { bg: 'rgba(236,72,153,0.15)',  text: '#f9a8d4' },
};

function RoleBadge({ role }) {
  const c = ROLE_COLORS[role] || ROLE_COLORS['reception'];
  return <span style={{ background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{role?.replace('-', ' ')}</span>;
}

function CreateStaffModal({ onClose, onSuccess, user, hospitals }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'reception', hospital_id: isSuperAdmin ? '' : user?.hospital_id, specialization: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSuperAdmin && !form.hospital_id) { setError('Please select a hospital'); return; }
    setLoading(true); setError('');
    try {
      const headers = await getAuthHeader();
      onSuccess(parseResponse(await axios.post(`${API_URL}/staff`, form, { headers })));
    } catch (err) { setError(err.response?.data?.error || 'Failed to create staff'); }
    finally { setLoading(false); }
  };

  const selectStyle = { flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', fontSize: 13, color: 'var(--text-primary)', background: 'rgba(255,255,255,0.06)', outline: 'none', width: '100%', boxSizing: 'border-box' };

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Add Staff Member</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
        </div>
        {error && <div style={errorAlert}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>Full Name</label><input style={inputStyle} placeholder="Staff full name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>Email</label><input style={inputStyle} type="email" placeholder="staff@hospital.com" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
          <div style={{ marginBottom: 14 }}><label style={labelStyle}>Phone</label><input style={inputStyle} placeholder="9000000000" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Role</label>
              <select style={selectStyle} value={form.role} onChange={e => setForm({...form, role: e.target.value, specialization: ''})}>
                {ROLES.map(r => <option key={r} value={r}>{r.replace('-', ' ')}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Hospital</label>
              {isSuperAdmin ? (
                <select style={selectStyle} value={form.hospital_id} onChange={e => setForm({...form, hospital_id: e.target.value})} required>
                  <option value="">— Select —</option>
                  {hospitals.map(h => <option key={h.hospital_id} value={h.hospital_id}>{h.name}</option>)}
                </select>
              ) : (
                <input style={{ ...inputStyle, opacity: 0.5 }} value={user?.hospital_id} disabled />
              )}
            </div>
          </div>
          {form.role === 'doctor' && (
            <div style={{ marginBottom: 14 }}><label style={labelStyle}>Specialization</label><input style={inputStyle} placeholder="e.g. Cardiologist" value={form.specialization} onChange={e => setForm({...form, specialization: e.target.value})} /></div>
          )}
          <div style={{ ...infoBox, marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: 'var(--accent-light)', fontWeight: 600, marginBottom: 4 }}>📧 Login credentials will be sent to their email</div>
            <div style={{ fontSize: 11, color: '#6366f1' }}>{form.role === 'doctor' ? 'Doctor profile will also be added to the appointments system.' : 'Staff will receive a temporary password and must change it on first login.'}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 2, ...primaryBtn(loading) }}>{loading ? 'Creating…' : 'Create Staff Account'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ViewStaffModal({ staff, onClose, onDeactivate, user }) {
  const [resetting, setResetting]     = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [resetDone, setResetDone]     = useState(false);
  const [error, setError]             = useState('');

  const handleResetPassword = async () => {
    if (!window.confirm(`Send a new temporary password to ${staff.email}?`)) return;
    setResetting(true); setError('');
    try { await axios.post(`${API_URL}/staff/${staff.staff_id}/reset-password`, {}, { headers: await getAuthHeader() }); setResetDone(true); }
    catch { setError('Failed to reset password. Please try again.'); }
    finally { setResetting(false); }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Deactivate ${staff.name}? They will lose access immediately.`)) return;
    setDeactivating(true);
    try { await axios.delete(`${API_URL}/staff/${staff.staff_id}`, { headers: await getAuthHeader() }); onDeactivate(staff.staff_id); onClose(); }
    catch { setError('Failed to deactivate staff'); }
    finally { setDeactivating(false); }
  };

  const details = [
    { label: 'Full Name', value: staff.name }, { label: 'Email', value: staff.email },
    { label: 'Phone', value: staff.phone || '—' }, { label: 'Hospital ID', value: staff.hospital_id },
    { label: 'Status', value: staff.is_active ? 'Active' : 'Inactive' }, { label: 'Created', value: staff.created_at?.slice(0, 10) },
    ...(staff.specialization ? [{ label: 'Specialization', value: staff.specialization }] : []),
  ];

  return (
    <div className="modal-overlay" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1000 }}>
      <div className="modal-box" style={{ ...modalWrap, width: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>Staff Details</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: '#475569', fontSize: 20 }}>✕</span>
        </div>

        <div style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6366f1', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Staff ID</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent-light)', fontFamily: 'monospace' }}>{staff.staff_id}</div>
          </div>
          <RoleBadge role={staff.role} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          {details.map((item, i) => (
            <div key={i} style={{ background: 'var(--bg-card-hover)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.value}</div>
            </div>
          ))}
        </div>

        {resetDone && (
          <div style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#4ade80', marginBottom: 2 }}>✅ Password reset successfully</div>
            <div style={{ fontSize: 12, color: '#166534' }}>A new temporary password was sent to <strong>{staff.email}</strong>.</div>
          </div>
        )}
        {error && <div style={errorAlert}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, ...cancelBtn }}>Close</button>
          <button onClick={handleResetPassword} disabled={resetting || resetDone}
            style={{ flex: 1, padding: '10px', borderRadius: 8, background: resetDone ? 'rgba(34,197,94,0.12)' : 'rgba(99,102,241,0.12)', border: `1px solid ${resetDone ? 'rgba(34,197,94,0.25)' : 'rgba(99,102,241,0.25)'}`, fontSize: 13, cursor: resetDone ? 'default' : 'pointer', color: resetDone ? '#4ade80' : 'var(--accent-light)', fontWeight: 600 }}>
            {resetting ? 'Sending…' : resetDone ? '✅ Sent' : '🔑 Reset Password'}
          </button>
          {staff.is_active && (
            <button onClick={handleDeactivate} disabled={deactivating}
              style={{ flex: 1, padding: '10px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', fontSize: 13, cursor: 'pointer', color: '#f87171', fontWeight: 600 }}>
              {deactivating ? 'Deactivating…' : '🚫 Deactivate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Staff({ user }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [staff, setStaff]                       = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [search, setSearch]                     = useState('');
  const [roleFilter, setRoleFilter]             = useState('all');
  const [showCreate, setShowCreate]             = useState(false);
  const [viewStaff, setViewStaff]               = useState(null);
  const [successMsg, setSuccessMsg]             = useState('');
  const [error, setError]                       = useState('');
  const [hospitals, setHospitals]               = useState([]);
  const [selectedHospital, setSelectedHospital] = useState('');

  const fetchHospitals = useCallback(async () => {
    try { const headers = await getAuthHeader(); setHospitals(parseResponse(await axios.get(`${API_URL}/hospitals`, { headers })).hospitals || []); } catch {}
  }, []);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      const params  = user?.role === 'hospital-admin' ? { hospital_id: user.hospital_id } : selectedHospital ? { hospital_id: selectedHospital } : {};
      setStaff(parseResponse(await axios.get(`${API_URL}/staff`, { headers, params })).staff || []);
    } catch { setError('Failed to load staff'); }
    finally { setLoading(false); }
  }, [user, selectedHospital]);

  useEffect(() => { if (isSuperAdmin) fetchHospitals(); fetchStaff(); }, []); // eslint-disable-line
  useEffect(() => { if (!isSuperAdmin) return; fetchStaff(); }, [selectedHospital]); // eslint-disable-line

  const handleCreateSuccess = (data) => {
    setShowCreate(false);
    const id = data.staff_id || data.staff?.staff_id || '';
    setSuccessMsg(`✅ Staff created${id ? ` — ID: ${id}` : ''}. Login credentials sent to their email.`);
    fetchStaff();
    setTimeout(() => setSuccessMsg(''), 8000);
  };

  const filtered = staff.filter(s => {
    const matchSearch = s.name?.toLowerCase().includes(search.toLowerCase()) || s.email?.toLowerCase().includes(search.toLowerCase()) || s.staff_id?.includes(search);
    return matchSearch && (roleFilter === 'all' || s.role === roleFilter);
  });

  const counts = { all: staff.length, active: staff.filter(s => s.is_active).length, doctor: staff.filter(s => s.role === 'doctor').length, reception: staff.filter(s => s.role === 'reception').length };
  const selectStyle = { flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', fontSize: 13, color: 'var(--text-primary)', background: 'rgba(255,255,255,0.06)', outline: 'none' };

  return (
    <div>
      {isSuperAdmin && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Hospital:</span>
          <select value={selectedHospital} onChange={e => { setSelectedHospital(e.target.value); setStaff([]); }} style={selectStyle}>
            <option value="">— All hospitals —</option>
            {hospitals.map(h => <option key={h.hospital_id} value={h.hospital_id}>{h.name} ({h.hospital_id})</option>)}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <input placeholder="Search by name, email or staff ID…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 220, padding: '9px 14px', borderRadius: 9, border: '1px solid var(--input-border)', background: 'var(--bg-input)', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }} />
        <button onClick={() => setShowCreate(true)} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 12px rgba(99,102,241,0.4)', whiteSpace: 'nowrap' }}>
          + Add Staff
        </button>
      </div>

      <div className="grid-4" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Staff', value: counts.all,       grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
          { label: 'Active',      value: counts.active,    grad: 'linear-gradient(135deg,#22c55e,#16a34a)' },
          { label: 'Doctors',     value: counts.doctor,    grad: 'linear-gradient(135deg,#8b5cf6,#ec4899)' },
          { label: 'Reception',   value: counts.reception, grad: 'linear-gradient(135deg,#f59e0b,#f97316)' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.grad, borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, marginBottom: 6, marginTop: 8 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {successMsg && <div style={successAlert}>{successMsg}</div>}
      {error && <div style={{ background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)', padding: '12px 16px', borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all','doctor','reception','nurse','hospital-admin'].map(r => (
          <button key={r} onClick={() => setRoleFilter(r)} style={pillBtn(roleFilter === r)}>{r.replace('-', ' ')}</button>
        ))}
      </div>

      <div className="page-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>Loading staff…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>{search ? 'No staff found' : 'No staff added yet'}</div>
        ) : (
          <div className="table-wrap">
            <table className="dark-table">
              <thead><tr>{['Staff ID','Name','Email','Phone','Role','Hospital','Status','Actions'].map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.staff_id || i}>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent-light)', fontWeight: 600 }}>{s.staff_id}</td>
                    <td><div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{s.name}</div>{s.specialization && <div style={{ fontSize: 11, color: '#475569' }}>{s.specialization}</div>}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{s.email}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{s.phone || '—'}</td>
                    <td><RoleBadge role={s.role} /></td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569' }}>{s.hospital_id}</td>
                    <td><span style={{ background: s.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)', color: s.is_active ? '#4ade80' : '#f87171', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{s.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td><button onClick={() => setViewStaff(s)} style={{ background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 7, padding: '4px 12px', fontSize: 11, cursor: 'pointer', color: 'var(--accent-light)', fontWeight: 600 }}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && <CreateStaffModal onClose={() => setShowCreate(false)} onSuccess={handleCreateSuccess} user={user} hospitals={hospitals} />}
      {viewStaff  && <ViewStaffModal staff={viewStaff} onClose={() => setViewStaff(null)} onDeactivate={(id) => setStaff(prev => prev.map(s => s.staff_id === id ? {...s, is_active: false} : s))} user={user} />}
    </div>
  );
}
